import { t } from 'i18next';
import { dataLibraryApi } from 'src/newServices/dataLibrary';
import type { RootState } from 'src/redux/store';
import type { IResourceProvider } from 'src/types/api';
import type { StringKeys } from 'src/types/base';
import {
  PROVIDER_STATUS,
  RESOURCE_PROVIDERS,
  RESOURCE_TYPE
} from 'src/types/enum';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { convertDate } from 'src/utils/utils';


const DESCRIPTION_KEY_MAP = {
  [RESOURCE_TYPE.NAMESPACE]: T.workspacesSecuredDescription,
  [RESOURCE_TYPE.PRIVATE_WORKSPACE]: T.myWorkspaceDescription,
  [RESOURCE_TYPE.PUBLIC_WORKSPACE]: T.publicWorkspaceDescription
};

export const getDataLibraryDescription = (currentParent) => {
  if (!currentParent?.type_code) {
    return '';
  }

  return DESCRIPTION_KEY_MAP[currentParent.type_code] ?? '';
};

export const RESOURCE_LABEL_KEY_MAP: Record<string, string> = {
  [RESOURCE_TYPE.WORKSPACE]: 'workspace',
  [RESOURCE_TYPE.NAMESPACE]: 'workspace',
  [RESOURCE_TYPE.COLLECTION]: 'folder',
  [RESOURCE_TYPE.FOLDER]: 'folder',
  [RESOURCE_TYPE.FILE]: 'file'
};

export const getNumberParam = (
  params: URLSearchParams,
  key: string,
  defaultValue: number
) => {
  const value = Number(params.get(key));
  return Number.isNaN(value) || !value ? defaultValue : value;
};

// The backend nests lock state under `lock_by: { locked, session_owner, session_name }`.
// session_owner/session_name may be absent (e.g. the locking user record no longer exists).
export interface ResourceLockInfo {
  locked?: boolean;
  session_owner?: string;
  session_name?: string;
}

export const isResourceLocked = (resource: any): boolean =>
  !!resource?.lock_by?.locked;

export const getResourceLockInfo = (resource: any): ResourceLockInfo => ({
  locked: !!resource?.lock_by?.locked,
  session_owner: resource?.lock_by?.session_owner,
  session_name: resource?.lock_by?.session_name
});

// A resource may be locked itself, or simply live under a folder that is -
// either way it can't be opened/edited/moved/deleted until the upload ends.
export const isBlockedByLock = (resource: any, currentParent: any): boolean =>
  isResourceLocked(resource) || isResourceLocked(currentParent);

export const getLockedModalData = (resource: any, currentParent: any) => ({
  display_name: resource?.display_name,
  session_owner:
    resource?.lock_by?.session_owner ?? currentParent?.lock_by?.session_owner,
  session_name:
    resource?.lock_by?.session_name ?? currentParent?.lock_by?.session_name
});

export const getInitials = (name: string) => {
  if (name) {
    const sanitizedStr = name
      ?.trim()
      .replace(/\s+/g, ' ')
      .replace(/[^a-zA-Z0-9' ]/g, ' ')
      .toUpperCase()
      .split(' ')
      .slice(0, 2);

    if (sanitizedStr.length === 1) {
      return sanitizedStr[0].charAt(0) + sanitizedStr[0].charAt(1);
    }
    return sanitizedStr.map((word) => word.charAt(0)).join('');
  } else {
    return '';
  }
};

export const getDocumentDeleteApiParams = (documents: any | any[]) => {
  const docs = Array.isArray(documents) ? documents : [documents];
  const idList = docs.map((doc) => doc.id);

  return {
    params: {
      id: idList
    }
  };
};

export const sortFilesAndFolders = (sites: any) => {
  const folders = sites.filter((site: any) => site.folder);
  const files = sites.filter((site: any) => site.file);

  const sortedFolders = folders.sort((a, b) => a.name.localeCompare(b.name));
  const sortedFiles = files.sort((a, b) => a.name.localeCompare(b.name));

  return !!sortedFolders?.length || !!sortedFiles.length
    ? [...sortedFolders, ...sortedFiles]
    : sites.sort((a, b) => a.name.localeCompare(b.name));
};

const getLatestActivitySource = (row: any) => {
  if (!row) {
    return null;
  }
  if (!!row.provider_id && (row.synchronized_on || row.synchronized_by)) {
    return {
      action: t(T.synced),
      user: row.synchronized_by,
      time: row?.synchronized_on,
      label: t(T.syncedBy)
    };
  }

  if (row.modified_on) {
    return {
      action: t(T.modified),
      user: row.modified_by,
      time: row.modified_on,
      label: t(T.modifiedBy)
    };
  }

  return null;
};

export const getLatestUpdatedByName = (row: any) =>
  getLatestActivitySource(row)?.user || '';

export const getLatestUpdatedByLabel = (row: any) =>
  getLatestActivitySource(row)?.label || '';

export const getLatestUpdatedAtTime = (row: any) => {
  const source = getLatestActivitySource(row);
  if (!source?.time) {
    return '';
  }
  return convertDate(new Date(source.time), t);
};

export const getLatestUpdatedAtDescription = (row: any) => {
  const source = getLatestActivitySource(row);
  if (!source?.time) {
    return '';
  }
  const formattedTime = convertDate(new Date(source.time), t);
  return `${source.action} ${formattedTime}`;
};

export const patchProcessDocumentsCache =
  (stateChanges: any | any[]) => (dispatch: any, getState: () => RootState) => {
    const updatedFiles = Array.isArray(stateChanges)
      ? stateChanges
      : [stateChanges];
    if (!updatedFiles.length) {
      return;
    }
    const state = getState();
    const apiState = state[dataLibraryApi.reducerPath];
    if (!apiState?.queries) {
      return;
    }

    const getDocumentsKeys = Object.keys(apiState.queries).filter((key) =>
      key.startsWith('getDocuments')
    );

    getDocumentsKeys.forEach((cacheKey) => {
      const queryState = apiState.queries[cacheKey];
      if (queryState?.status === 'fulfilled' && queryState.originalArgs) {
        dispatch(
          dataLibraryApi.util.updateQueryData(
            'getDocuments',
            queryState.originalArgs,
            (draft) => {
              if (!draft?.data) {
                return;
              }

              updatedFiles.forEach((updatedFile) => {
                const fileIndex = draft.data.findIndex(
                  (f: any) => f.id === updatedFile.id
                );
                if (fileIndex !== -1) {
                  draft.data[fileIndex] = {
                    ...draft.data[fileIndex],
                    ...updatedFile,
                    status: updatedFile?.status_name
                  };
                }
              });
            }
          )
        );
      }
    });
  };

export const patchSyncDocumentsCache =
  (stateChanges: any | any[]) => (dispatch: any, getState: () => RootState) => {
    const updatedFiles = Array.isArray(stateChanges?.all_ids)
      ? stateChanges?.all_ids
      : [stateChanges];
    if (!updatedFiles.length) {
      return;
    }
    const state = getState();
    const apiState = state[dataLibraryApi.reducerPath];
    if (!apiState?.queries) {
      return;
    }

    const getDocumentsKeys = Object.keys(apiState.queries).filter((key) =>
      key.startsWith('getDocuments')
    );
    const { all_ids, parent_id, ...syncStatusData } = stateChanges;
    const idsToInvalidate = [];
    getDocumentsKeys.forEach((cacheKey) => {
      const queryState = apiState.queries[cacheKey];
      if (queryState?.status === 'fulfilled' && queryState.originalArgs) {
        dispatch(
          dataLibraryApi.util.updateQueryData(
            'getDocuments',
            queryState.originalArgs,
            (draft) => {
              if (!draft?.data) {
                return;
              }
              updatedFiles.forEach((updatedFile) => {
                if (Array.isArray(draft?.data)) {
                  const fileIndex = draft.data.findIndex(
                    (f: any) => f.id === updatedFile
                  );
                  if (fileIndex !== -1) {
                    draft.data[fileIndex] = {
                      ...draft.data[fileIndex],
                      ...syncStatusData
                    };
                  }
                }
                if (Array.isArray(draft?.parent_hierarchy)) {
                  const hierarchyIndex = draft.parent_hierarchy.findIndex(
                    (h: any) => h.id === updatedFile
                  );
                  if (hierarchyIndex !== -1) {
                    idsToInvalidate.push(
                      draft.parent_hierarchy[hierarchyIndex].id
                    );
                    draft.parent_hierarchy[hierarchyIndex] = {
                      ...draft.parent_hierarchy[hierarchyIndex],
                      ...syncStatusData
                    };
                  }
                }
              });
            }
          )
        );
      }
    });
    const isSyncCompleted =
      syncStatusData.sync_status_code === PROVIDER_STATUS.SYNC_COMPLETED;
    if (isSyncCompleted) {
      const tagsToInvalidate = idsToInvalidate.map((uId) => ({
        type: 'Files' as const,
        id: uId
      }));
      dispatch(dataLibraryApi.util.invalidateTags(tagsToInvalidate));
    }
  };

// The current folder's own lock state comes straight from its
// `parent_hierarchy` entry's `lock_by` - the backend populates that directly,
// so it no longer needs to be inferred from whether any child item (in
// `documents.data`) happens to be locked.
export const getHierarchyMetadata = (parentHierarchy: any[] = []) => {
  const length = parentHierarchy.length;
  if (length === 0) {
    return { currentParent: null, grandParent: null, currentWorkspace: null };
  }

  return {
    currentParent: parentHierarchy.at(-1) ?? null,
    grandParent: length > 2 ? parentHierarchy.at(-2) : null,
    currentWorkspace: length > 1 ? parentHierarchy[1] : null
  };
};

export const MOVEABLE_RESOURCES = [RESOURCE_TYPE.FOLDER, RESOURCE_TYPE.FILE];

export enum EXEMPTED_WORKSPACES_COLLECTIONS {
  FOUND = RESOURCE_TYPE.FOUND_WORKSPACE,
  PUBLIC = RESOURCE_TYPE.PUBLIC_WORKSPACE,
  PRIVATE = RESOURCE_TYPE.PRIVATE_WORKSPACE,
  FOUND_COLLECTION = RESOURCE_TYPE.FOUND_COLLECTION
}

export const getCurrentResourceProvider = (
  document: any,
  userResourceProviders: IResourceProvider[]
): IResourceProvider | null => {
  if (!document?.provider_id || !userResourceProviders?.length) {
    return null;
  }

  return (
    userResourceProviders.find((p) => p?.id === document.provider_id) ?? null
  );
};

export const getResourceProviderNavigateLink = (
  itemData: StringKeys,
  resourceProvider: IResourceProvider
): string => {
  const { type, base_url } = resourceProvider ?? {};

  switch (type) {
    case RESOURCE_PROVIDERS.SHAREPOINT:
    case RESOURCE_PROVIDERS.ONEDRIVE:
    case RESOURCE_PROVIDERS.GOOGLEDRIVE:
      return itemData?.webUrl ?? '';

    case RESOURCE_PROVIDERS.CONFLUENCE:
      return itemData?._links?.webui
        ? `${base_url}${itemData._links.webui}`
        : '';

    default:
      return '';
  }
};

